/**
 * AICar class represents an AI-controlled car in a 3D scene.
 * It manages the movement, lane changing, and speed control of the car.
 */
import * as THREE from 'three';
import EventDispatcher from "../common/EventDispatcher";
import { Global } from "../common/global";
import { gsap, Cubic } from 'gsap';

const CAR_SCALE= .25;
// Constants for controlling car movement and speed
const CAR_SPEED_LINEAR_FACT = .000065; // AI car acceleration
const CAR_BRAKE_FACTOR = 0.01*Math.random();
const CAR_MAX_SPEED_LIMIT = 0.7; // AI car max speed


export default class AICar {
    /**
     * Initializes an AICar instance.
     * @param {THREE.Scene} scene - The scene where the car will be added.
     */
    constructor(scene) {
        this.scene = scene;
        // Initialize various properties of the car
        this.speedUpdateDirection = 0;
        this.canAIMove = true;
        this.initZ = 0;
        this.speed = 0;
        this.wheel_back = null;
        this.wheel_front = null;
        this.reduceSpeedToZero = false;
        this.reachedAtFinish= false;
        this.laneNo = 1;
        this.laneLerpFactor = { 'value': 1 };
        this.lightMaterial = null;
        this.brakeApplied = false;
        this.lightIntensity = { 'value': 0 };
        this.breakLightDelay = 0;
        this.wheelDirn = 1;
        this.wheelNextDirn = 1;
        this.canCheckLaneCollision = true;

        this.carBoxSize={
            "AICar1":{x:1, y:3, z:2.35},
            "AICar2":{x:1, y:3, z:2.35},
            "AICar3":{x:1, y:3, z:2.35},
            "AICar4":{x:0.7, y:3, z:1.75},
            "AICar5":{x:0.9, y:3, z:2.2},
            "AIBus":{x:1.0, y:3, z:3.85},
            "AIVan":{x:1.0, y:3, z:2.48}
        }

        this.onEvents= this.onEvents.bind(this)
    }

    /**
     * Initializes the AI car with necessary parameters.
     * * @param {string} carIndex - The index identifier for the car.
     * @param {string} carKey - The key identifier for the car.
     * @param {number} pathTime - The time parameter for the car's path.
     * @param {number} speedLinearAdd - The linear speed addition for the car.
     * @param {number} laneNo - The lane number where the car starts.
     */
    init(carIndex, carKey, pathTime, speedLinearAdd, laneNo) {
        this.carKey = carKey;
        this.pathTime = pathTime;
        this.initPathTime= pathTime;
        this.carIndex=carIndex;
        this.laneNo = laneNo;
        this.speedLinearAdd = speedLinearAdd*(this.initPathTime<=0.01?5+Math.random()*10:1);

        this.events = EventDispatcher.getObj();
        this.events.addEventListener("PLAYER_EVENTS", this.onEvents);
        this.events.addEventListener("GAMESCENE_EVENTS", this.onEvents);
        this.events.addEventListener("GAME_EVENTS", this.onEvents);

        this.carModel = null;

        this.raycaster = new THREE.Raycaster();
        this.raycaster.firstHitOnly = true;
        this.raycaster.far = 10;

        this.raycaster2 = new THREE.Raycaster();
        this.raycaster2.firstHitOnly = true;
        this.raycaster2.far = 10;
    }

    /**
     * Changes the lane of the car.
     */
    changeLane() {
        this.laneNo = (this.laneNo == 1) ? 2 : 1;
        this.laneLerpFactor['value'] = .05;
        this.laneLerpTwn && this.laneLerpTwn.kill();
        this.laneLerpTwn = gsap.to(this.laneLerpFactor, {
            duration: 2,
            value: .1,
            repeat: 0,
            yoyo: false,
            ease: Cubic.easeInOut,
            onComplete: function () {
                this.canCheckLaneCollision = true;
            }.bind(this)
        })
        this.canCheckLaneCollision = false;
    }

    /**
     * Handles various events related to the AI car.
     * @param {Object} data - Data associated with the event.
     */
    onEvents(data) {
        switch (data['message']['event_type']) {
            case "reset_game":
                this.reset();
                break;
            case "add_car":
                this.addCar();
                this.setPath();
                break;
            case "update":
                this.update(data['message']['data']);
                break;
            case "start_AI":
                this.startAIMove();
                break;
            case "check_speed_control":
                this.checkForSpeedControl(data['message']['data']);
                break;
            case "dispose":
                this.dispose();
                break;
        }
    }

    /**
     * Starts the movement of the AI car.
     */
    startAIMove() {
        this.canAIMove = true;
        this.speedUpdateDirection = 1;
    }
    reset(){
        this.canAIMove = false;
        this.speedUpdateDirection = 0;
        this.initZ = 0;
        this.speed = 0;
        this.reduceSpeedToZero = false;
        this.reachedAtFinish= false;
        this.laneLerpFactor = { 'value': 1 };
        this.brakeApplied = false;
        this.lightIntensity = { 'value': 0 };
        this.breakLightDelay = 0;
        this.wheelDirn = 1;
        this.wheelNextDirn = 1;
        this.canCheckLaneCollision = true;
        // Global.carPositionIndex[this.carKey] = 0;
        this.pathTime= this.initPathTime;
        this.reachedAtFinish= false;
        this.showOrHiTailLight(0);
    }
    /**
     * Sets the path for the AI car.
     */
    setPath() {

        this.initCarMoveX = Global[`AIPath${this.laneNo}`].getPoint(this.pathTime).x;

    }

    /**
     * Checks for speed control of the AI car based on obstacles and lane conditions.
     * @param {Object} data - Data associated with the speed control.
     */
    checkForSpeedControl(data) {
        if (this.carModel == null) return false;

        this.carBox.updateMatrixWorld();


        let intersects1 = null;
        let intersects2 = null;
        // const intersects3 = this.raycaster.intersectObjects(otherCars, false);

        if(!this.reachedAtFinish){
            this.raycaster2.ray.origin.copy(this.carModel.position.clone());
            this.raycaster2.ray.direction.copy(new THREE.Vector3(0, -1, 0));
            const intersectsOnFinish = this.raycaster2.intersectObjects(data['finishLine'], true);
            if(intersectsOnFinish.length>0){
                // console.log("AI Finish Reached");
                this.reachedAtFinish= true;
            }
            //reachedAtFinish
        }

        // if (this.speedUpdateDirection === 1) {

        //     intersects1 = this.raycaster.intersectObjects(Global.speedLimiters, false);
            
        //     if (intersects1.length > 0) {
        //         if (intersects1[0].distance <= 10) {
        //             this.speedUpdateDirection = -1;
        //             if (!this.brakeApplied) {
        //                 this.brakeApplied = true;
        //                 this.breakLightDelay = 0;
        //                 this.showOrHiTailLight(10000);
        //             }
        //             this.wheelNextDirn = intersects1[0].object.userData['direction'] == "left" ? -1 : 1;
        //         }
        //     }
        // } else{
        //     intersects2 = this.raycaster.intersectObjects(Global.speedActivators, false);
        //     if (intersects2.length > 0) {
        //         if (intersects2[0].distance <= 10) {
        //             this.speedUpdateDirection = 1;
        //         }
        //     }
        // }

        // if (intersects3 && intersects3.length > 0 && intersects3[0].distance <= LANE_CHANGE_THRESHOLD_DISTANCE && this.canCheckLaneCollision /* && this.speedUpdateDirection == 1 */) {
        //     if(this.reachedAtFinish) return false;
        //     this.changeLane();
        // }
        // if (intersects3 && intersects3.length > 0 && intersects3[0].distance <= LANE_CHANGE_THRESHOLD_DISTANCE && this.speedUpdateDirection == -1) {
        //     this.reduceSpeedToZero = true;
        // }
        // if (this.speedUpdateDirection !== -1 || (intersects3 && (intersects3.length == 0 || (intersects3.length > 0 && intersects3[0].distance > LANE_CHANGE_THRESHOLD_DISTANCE)))) {
        //     this.reduceSpeedToZero = false;
        // }
    }

    /**
     * Shows or hides the tail light of the car.
     * @param {number} value - The intensity value for the tail light.
     */
    showOrHiTailLight(value) {
        this.lightTwn && this.lightTwn.kill();
        this.lightTwn = gsap.to(this.lightIntensity, {
            duration: value == 0 ? 0.25 : 0.5,
            value: value,
            repeat: 0,
            yoyo: false,
            ease: value == 0 ? Cubic.easeOut : Cubic.easeIn,
            onUpdate: function () {
                this.lightMaterial && (this.lightMaterial.emissiveIntensity = this.lightIntensity['value']);
            }.bind(this)
        })
    }

    /**
     * Updates the state of the AI car.
     * @param {Object} data - Data associated with the update.
     */
    update(data) {
        if(Global.gamePaused) return false;
        this.breakLightDelay += data['now'] || 0;
        if (this.breakLightDelay >= 3000000 && this.brakeApplied) {
            this.brakeApplied = false;
            this.showOrHiTailLight(0);
        }
        if (this.carModel != null) {
            this.carBox.position.set(...this.carModel.position);
            this.carBox.rotation.set(...this.carModel.rotation);

            if (Global[`AIPath${this.laneNo}`]) {

                if (this.canAIMove /* && !this.reachedAtFinish */) {
                    this.pathTime += CAR_SPEED_LINEAR_FACT * this.speed * Global.delta;
                    if(this.pathTime>=1){
                        // console.log("AI Finish Reached");
                        this.reachedAtFinish=true;
                    }
                    // console.log(this.pathTime)
                    this.pathTime= Math.min(1,this.pathTime)
                    // Global.carPositionIndex[this.carKey] = this.pathTime;
                }
                if (this.speedUpdateDirection == 1 && this.canCheckLaneCollision && !this.reachedAtFinish && this.canAIMove) {
                    this.speed += this.speedLinearAdd* Global.delta;
                    this.speed = Math.min(this.speed, CAR_MAX_SPEED_LIMIT);
                } else if (this.reachedAtFinish || ((this.speedUpdateDirection == -1 || !this.canCheckLaneCollision) && this.canAIMove)) {
                    this.speed -= this.speed * (!this.reachedAtFinish?CAR_BRAKE_FACTOR:CAR_BRAKE_FACTOR*.01) * Global.delta;
                    this.speed = Math.max(this.speed, (this.reduceSpeedToZero|| this.reachedAtFinish) ? 0 : .25);
                }

                this.aiPos = Global[`AIPath${this.laneNo}`].getPoint(this.pathTime);
                this.aiPos2 = Global[`AIPath${this.laneNo}`].getPoint(this.pathTime + .001/* this.speedLinearAdd / 10 */);

                // this.frontWheel1.rotation.x += .3 * this.speed * Global.delta;
                // this.frontWheel2.rotation.x += .3 * this.speed * Global.delta;
                this.wheel_back.rotation.x += 2.7 * this.speed * Global.delta;
                this.wheel_front.rotation.x += 2.7 * this.speed * Global.delta;
                
                let nextPosX = THREE.MathUtils.lerp(this.carModel.position.x, this.aiPos.x, Math.min(this.laneLerpFactor['value'] * Global.delta, 1));
                let nextPosY = THREE.MathUtils.lerp(this.carModel.position.y, this.aiPos.y + 0.05 , Math.min(this.laneLerpFactor['value'] * Global.delta, 1));
                let nextPosZ = THREE.MathUtils.lerp(this.carModel.position.z, this.aiPos.z, Math.min(this.laneLerpFactor['value'] * Global.delta, 1));

                let nextLookAtPosX = THREE.MathUtils.lerp(this.carModel.position.x, this.aiPos2.x, Math.min(this.laneLerpFactor['value'] * Global.delta, 1));
                let nextLookAtPosY = THREE.MathUtils.lerp(this.carModel.position.y, this.aiPos2.y + 0.05 , Math.min(this.laneLerpFactor['value'] * Global.delta, 1));
                let nextLookAtPosZ = THREE.MathUtils.lerp(this.carModel.position.z, this.aiPos2.z, Math.min(this.laneLerpFactor['value'] * Global.delta, 1));

                let nextPos = new THREE.Vector3(nextPosX, nextPosY, nextPosZ);
                let nextLookAtPos = new THREE.Vector3(nextLookAtPosX, nextLookAtPosY, nextLookAtPosZ);
                this.carModel.position.copy(nextPos);

                this.carModel.lookAt(nextLookAtPos)
                if (this.canAIMove) {
                    this.wheelDirn = THREE.MathUtils.lerp(this.wheelDirn, this.wheelNextDirn, Math.min(.015 * Global.delta, 1));
                    // this.frontWheel1.rotation.y = (this.carModel.rotation.y - this.initZ) * .5 * this.wheelDirn;
                    // this.frontWheel2.rotation.y = (this.carModel.rotation.y - this.initZ) * .5 * this.wheelDirn;
                } else {
                    this.initZ = this.carModel.rotation.y;
                }
            }
        }
      
    }

    /**
     * Adds the AI car to the scene.
     */
    addCar() {
        this.carModel = Global.assets['glbs'][this.carKey]['asset'].scene.clone();
        this.scene.add(this.carModel);
        this.carModel.scale.set(CAR_SCALE, CAR_SCALE, CAR_SCALE);
        this.carModel.rotation.set(-Math.PI / 2, 0, Math.PI / 2);

        this.carBox = new THREE.Mesh(new THREE.BoxGeometry(this.carBoxSize[this.carKey].x, this.carBoxSize[this.carKey].y, this.carBoxSize[this.carKey].z), new THREE.MeshPhysicalMaterial({
            color: '#ff0000',
            transparent: true,
            opacity: 0.0,
            depthWrite: false
        }));
        this.scene.add(this.carBox);

        this.carBox.userData = { 'carKey': this.carKey };
        Global.cars.push(this.carBox);
        this.carModel.traverse(function (child) {
            if (child.name.indexOf('light') != -1 && child.name.indexOf("Front_light") == -1) {
                if (this.lightMaterial === null) {
                    this.lightMaterial = child.material.clone();
                    this.lightMaterial.color = new THREE.Color(0.25, 0, 0)
                    this.lightMaterial.emissive = new THREE.Color(1, 0, 0)
                    this.lightMaterial.emissiveIntensity = 0;
                }

                child.material = this.lightMaterial;
            }
            child.castShadow = true;
            if (child.name == 'wheel_front') {
                // child.rotation.order = "YZX";
                this.wheel_front = child;
            }
            if (child.name == 'wheel_back') {
                // child.rotation.order = "YZX";
                this.wheel_back = child;
            }
            if(child.material&& child.material.name.toLowerCase().indexOf("shadow")==-1){
                child.material.envMap=Global.envMap
                child.material.envMapIntensity = 2;
               }else if(child.material){
                child.material.envMapIntensity = 1;
           
               }
            
        }.bind(this));

       
    }
    dispose(){
        this.scene.remove(this.carModel);
        
        this.carModel && this.carModel.traverse(function (child) {
            if(child.geometry){
                child.geometry.dispose();
                child.geometry.dispose();
            }
        });
        this.events.removeEventListener("PLAYER_EVENTS", this.onEvents);
        this.events.removeEventListener("GAMESCENE_EVENTS", this.onEvents);
        this.events.removeEventListener("GAME_EVENTS", this.onEvents);

        
    }
}
